import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../lib/supabaseClient';
import { API_BASE_URL } from '../config/api';

const ADMIN_API_BASE = `${API_BASE_URL}/api/admin`;
const SUSPENSION_CACHE_KEY = 'adminMoverSuspensionStatus';

const readSuspensionCache = () => {
  if (typeof window === 'undefined') return {};

  try {
    const rawValue = localStorage.getItem(SUSPENSION_CACHE_KEY);
    if (!rawValue) return {};

    const parsedValue = JSON.parse(rawValue);
    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch {
    return {};
  }
};

const writeSuspensionCache = (cache) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(SUSPENSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
  }
};

const createStableCompletedMoves = (mover) => {
  const seed = `${mover.id || ''}${mover.company_name || ''}`;
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }

  return 100 + (hash % 201);
};

const INVALID_DOC_VALUES = new Set([
  'n/a',
  'na',
  'n\\a',
  'none',
  'null',
  'nil',
  'pending',
  'not provided',
  'not available',
  '-',
  '--',
  '---',
  'tbd',
]);

const hasMeaningfulDocumentValue = (value) => {
  if (value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return false;

  return !INVALID_DOC_VALUES.has(normalized);
};

const calculateProfileCompletion = ({ mover, hasApprovedInsurance, hasApprovedLicense }) => {
  let completed = 0;
  const total = 10; // Google profile link is optional and not counted

  if (mover.company_name) completed++;
  if (mover.company_description) completed++;
  if (mover.website_link) completed++;
  if (mover.company_logo) completed++;
  if (mover.phone) completed++;
  if (mover.email) completed++;
  if (mover.business_address) completed++;
  if (hasApprovedInsurance) completed++;
  if (hasApprovedLicense) completed++;
  if (mover.city && mover.state) completed++;

  return Math.round((completed / total) * 100);
};

const TopMovers = () => {
  const navigate = useNavigate();
  const [movers, setMovers] = useState([]);
  const [peekMovers, setPeekMovers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopMovers = async () => {
      try {
        setLoading(true);

        // Fetch active movers for homepage cards.
        const { data, error } = await supabase
          .from('movers_data')
          .select('id, company_name, company_logo, years_in_business, is_verified, email, company_description, website_link, phone, business_address, city, state')
          .eq('is_active', true)
          .eq('account_paused', false);

        if (error) {
          throw error;
        }

        const suspensionCache = readSuspensionCache();
        const suspendedEmails = new Set(
          Object.entries(suspensionCache)
            .filter(([, isSuspended]) => Boolean(isSuspended))
            .map(([email]) => email)
        );

        try {
          const emails = (data || []).map((mover) => mover.email).filter(Boolean);

          if (emails.length > 0) {
            const response = await fetch(`${ADMIN_API_BASE}/get-mover-ban-status`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ emails }),
            });

            if (response.ok) {
              const result = await response.json();

              if (result.success && result.banStatus) {
                const updatedCache = { ...suspensionCache };

                Object.entries(result.banStatus).forEach(([email, status]) => {
                  const isSuspended = Boolean(status?.banned);
                  updatedCache[email] = isSuspended;

                  if (isSuspended) {
                    suspendedEmails.add(email);
                  } else {
                    suspendedEmails.delete(email);
                  }
                });

                writeSuspensionCache(updatedCache);
              }
            }
          }
        } catch {
        }

        const visibleMovers = (data || []).filter((mover) => !suspendedEmails.has(mover.email));

        const visibleMoverIds = visibleMovers.map((mover) => mover.id);
        let approvedInsuranceByMover = new Set();
        let approvedLicenseByMover = new Set();

        if (visibleMoverIds.length > 0) {
          const [{ data: insuranceData, error: insuranceError }, { data: licenseData, error: licenseError }] = await Promise.all([
            supabase
              .from('mover_insurance')
              .select('mover_id, policy_number')
              .in('mover_id', visibleMoverIds)
              .eq('status', 'approved'),
            supabase
              .from('mover_licenses')
              .select('mover_id, license_number')
              .in('mover_id', visibleMoverIds)
              .eq('status', 'approved')
          ]);

          if (insuranceError) throw insuranceError;
          if (licenseError) throw licenseError;

          approvedInsuranceByMover = new Set(
            (insuranceData || [])
              .filter((item) => hasMeaningfulDocumentValue(item.policy_number))
              .map((item) => item.mover_id)
          );

          approvedLicenseByMover = new Set(
            (licenseData || [])
              .filter((item) => hasMeaningfulDocumentValue(item.license_number))
              .map((item) => item.mover_id)
          );
        }

        const profileCompleteMovers = visibleMovers.filter((mover) => {
          const hasApprovedInsurance = approvedInsuranceByMover.has(mover.id);
          const hasApprovedLicense = approvedLicenseByMover.has(mover.id);

          // Strict requirement: both approved insurance and approved license must exist.
          if (!hasApprovedInsurance || !hasApprovedLicense) {
            return false;
          }

          const completion = calculateProfileCompletion({
            mover,
            hasApprovedInsurance,
            hasApprovedLicense,
          });

          return completion === 100;
        });

        const moverIds = profileCompleteMovers.map((mover) => mover.id);
        let reviewsByMover = {};

        if (moverIds.length > 0) {
          const { data: reviewsData, error: reviewsError } = await supabase
            .from('mover_reviews')
            .select('mover_id, rating')
            .in('mover_id', moverIds)
            .eq('is_approved', true);

          if (reviewsError) {
            throw reviewsError;
          }

          reviewsByMover = (reviewsData || []).reduce((accumulator, review) => {
            const current = accumulator[review.mover_id] || { sum: 0, count: 0 };
            current.sum += review.rating;
            current.count += 1;
            accumulator[review.mover_id] = current;
            return accumulator;
          }, {});
        }

        const moversWithRatings = profileCompleteMovers.map((mover) => {
          const ratingData = reviewsByMover[mover.id] || { sum: 0, count: 0 };
          const avgRating = ratingData.count > 0 ? ratingData.sum / ratingData.count : 0;

          return {
            id: mover.id,
            name: mover.company_name || 'Moving Company',
            rating: avgRating,
            reviews: ratingData.count,
            image: mover.company_logo || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400',
            experience: mover.years_in_business || 0,
            completed: createStableCompletedMoves(mover),
            isVerified: mover.is_verified || false,
          };
        });

        // Sort by rating (descending) and then by review count (descending)
        const sortedMovers = moversWithRatings
          .sort((a, b) => {
            if (b.rating !== a.rating) {
              return b.rating - a.rating;
            }
            return b.reviews - a.reviews;
          });

        const top4 = sortedMovers.slice(0, 4);
        // Use extra movers for peek row, or reverse top 4 as fallback
        const peek = sortedMovers.length > 4
          ? sortedMovers.slice(4, 8)
          : [...top4].reverse();

        setMovers(top4);
        setPeekMovers(peek);
      } catch (error) {
        // Fallback to empty array on error
        setMovers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopMovers();
  }, []);

  return (
    <section className="py-14 sm:py-16 bg-gradient-to-b from-white to-gray-50/50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-primary font-bold tracking-widest uppercase text-sm mb-4 block">Trusted Partners</span>
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">Top Rated Moving Companies</h2>
          <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
            Connect with verified, professional movers trusted by thousands of customers for a stress-free transition.
          </p>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-3xl shadow-[0_4px_20px_rgb(0,0,0,0.05)] overflow-hidden border border-gray-100/80 animate-pulse">
                <div className="h-56 bg-gray-200"></div>
                <div className="p-7">
                  <div className="h-6 bg-gray-200 rounded mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    <div className="h-8 bg-gray-200 rounded"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : movers.length === 0 ? (
          <div className="text-center py-10 mb-10">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-gray-500 text-lg mb-2">No movers available at the moment</p>
            <p className="text-gray-400 text-sm">Check back soon or contact us to add moving companies!</p>
          </div>
        ) : (
          /* Main container with border */
          <div className="bg-white rounded-3xl border border-gray-200 shadow-[0_10px_34px_rgba(15,23,42,0.06)] overflow-hidden">
            {/* Inner content with padding */}
            <div className="relative p-8 pb-24">
              {/* Movers Grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {movers.map((mover, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-2xl shadow-sm overflow-hidden border border-gray-100 flex flex-col hover:shadow-[0_14px_30px_rgba(15,23,42,0.09)] transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="relative h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={mover.image}
                        alt={mover.name}
                        className="w-full h-full object-cover"
                      />

                      <div className="absolute top-3 left-3 flex flex-col gap-2">
                        {mover.isVerified && (
                          <div className="bg-green-500/95 backdrop-blur-md text-white text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider flex items-center shadow-lg border border-white/20">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Verified
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-5 flex flex-col flex-grow bg-white">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{mover.name}</h3>
                      <div className="flex items-center mb-4">
                        <div className="flex items-center bg-orange-50 px-2 py-1 rounded-lg">
                          <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-primary font-bold text-sm ml-1">{mover.rating > 0 ? mover.rating.toFixed(1) : 'New'}</span>
                        </div>
                        <span className="text-gray-400 text-xs ml-2 font-medium">{mover.reviews} {mover.reviews === 1 ? 'Review' : 'Verified Reviews'}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 py-3 border-t border-gray-100 mt-auto">
                        <div className="flex flex-col">
                          <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-0.5">Experience</span>
                          <span className="text-sm font-bold text-gray-800">{mover.experience} Years</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-0.5">Completed</span>
                          <span className="text-sm font-bold text-gray-800">{mover.completed}+</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Peek of real movers - blurred to entice users */}
              {/* Desktop: show all peek movers in grid */}
              <div className="mt-6 hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6" aria-hidden="true">
                {peekMovers.map((mover, index) => (
                  <div
                    key={`peek-${mover.id || index}`}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm select-none"
                    style={{ filter: 'blur(3px)', pointerEvents: 'none' }}
                  >
                    <div className="h-36 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden flex items-center justify-center">
                      {mover.image ? (
                        <img
                          src={mover.image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-500">
                            {mover.name?.charAt(0) || 'M'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-bold text-gray-800 mb-2 truncate">{mover.name}</h3>
                      <div className="flex items-center gap-1.5 mb-3">
                        <span className="text-yellow-500 text-sm">★</span>
                        <span className="text-sm font-semibold text-gray-700">{mover.rating}</span>
                        <span className="text-xs text-gray-400">({mover.reviews})</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">{mover.experience} Yrs Exp</span>
                        <span className="text-xs text-gray-500">{mover.completed}+ Jobs</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mobile: show only 1 blurred peek card */}
              {peekMovers.length > 0 && (
                <div className="mt-6 sm:hidden" aria-hidden="true">
                  <div
                    className="bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm select-none"
                    style={{ filter: 'blur(3px)', pointerEvents: 'none' }}
                  >
                    <div className="h-36 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden flex items-center justify-center">
                      {peekMovers[0].image ? (
                        <img
                          src={peekMovers[0].image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-500">
                            {peekMovers[0].name?.charAt(0) || 'M'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-bold text-gray-800 mb-2 truncate">{peekMovers[0].name}</h3>
                      <div className="flex items-center gap-1.5 mb-3">
                        <span className="text-yellow-500 text-sm">★</span>
                        <span className="text-sm font-semibold text-gray-700">{peekMovers[0].rating}</span>
                        <span className="text-xs text-gray-400">({peekMovers[0].reviews})</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">{peekMovers[0].experience} Yrs Exp</span>
                        <span className="text-xs text-gray-500">{peekMovers[0].completed}+ Jobs</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fade overlay - starts higher */}
              <div 
                className="absolute bottom-0 left-0 right-0 h-80 pointer-events-none z-10"
                style={{
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.3) 15%, rgba(255,255,255,0.7) 35%, rgba(255,255,255,0.95) 55%, rgb(255,255,255) 70%)'
                }}
              />

              {/* View More Button - Positioned higher, overlapping peek cards */}
              <div className="absolute bottom-24 left-0 right-0 flex justify-center z-20">
                <button
                  onClick={() => navigate('/quote')}
                  className="group relative flex min-h-[54px] items-center gap-3 bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white pl-8 pr-6 rounded-full font-semibold text-base transition-all duration-300 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.42)] hover:-translate-y-1"
                >
                  <span className="text-[15px] tracking-wide">View All Movers</span>
                  <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-full group-hover:bg-white/20 transition-all duration-300 group-hover:scale-110">
                    <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TopMovers;
